import React from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Modal, ScrollView, StyleSheet, Text, TouchableOpacity, View } from './rn';
import MessageReactionPicker from './MessageReactionPicker';
import { useTheme } from '../theme/ThemeContext';
import type { Reaction } from '../utils/reactions';

type Props = { visible: boolean; messageId?: number; userId?: number; mine: boolean; preview: string;
  reactions?: Reaction[]; details?: boolean; onSelect: (emoji: string) => void; onClose: () => void; children: React.ReactNode };

/** Presentation only. The caller retains every action's eligibility and side effects. */
export default function MessageActionsMenu({ visible, messageId, userId, mine, preview, reactions, details = false, onSelect, onClose, children }: Props) {
  const colors = useTheme(); const insets = useSafeAreaInsets();
  const side = { alignSelf: mine ? 'flex-end' as const : 'flex-start' as const };
  const surface = { backgroundColor: colors.white, borderColor: colors.gray200 };
  return <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
    <View style={s.overlay} accessibilityViewIsModal onAccessibilityEscape={onClose}>
      <TouchableOpacity testID="message-actions-backdrop" style={StyleSheet.absoluteFill} accessible={false} activeOpacity={1} onPress={onClose} />
      <View pointerEvents="box-none" style={[s.safe, { paddingTop: insets.top + 16, paddingBottom: insets.bottom + 16 }]}>
        <ScrollView pointerEvents="box-none" style={s.scroll} contentContainerStyle={s.stack} keyboardShouldPersistTaps="handled" bounces={false}>
          {!details && <View testID="message-actions-reactions" style={[s.reactions, surface, side]}>
            {visible && <MessageReactionPicker key={`${userId}:${messageId}`} userId={userId} reactions={reactions} onSelect={onSelect} />}
          </View>}
          {!details && !!preview && <View testID="message-actions-preview" style={[s.preview, { backgroundColor: colors.gray100 }, side]}>
            <Text numberOfLines={4} style={[s.previewText, { color: colors.black }]}>{preview}</Text>
          </View>}
          <View testID="message-actions-list" style={[s.actions, surface, side]}>{children}</View>
        </ScrollView>
      </View>
    </View>
  </Modal>;
}

export function MessageActionRow({ testID, label, icon, onPress, disabled = false, note }: {
  testID: string; label: string; icon: React.ComponentProps<typeof Ionicons>['name']; onPress: () => void; disabled?: boolean; note?: string;
}) {
  const colors = useTheme();
  return <TouchableOpacity testID={testID} accessibilityRole="button" accessibilityLabel={label}
    accessibilityState={{ disabled }} disabled={disabled} onPress={onPress} style={[s.row, disabled && s.disabled]}>
    <Ionicons name={icon} size={23} color={colors.black} />
    <View style={s.label}><Text style={[s.text, { color: colors.black }]}>{label}</Text>
      {!!note && <Text style={[s.note, { color: colors.gray600 }]}>{note}</Text>}</View>
  </TouchableOpacity>;
}
const s = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.28)', justifyContent: 'center' },
  safe: { maxHeight: '100%', paddingHorizontal: 16 }, scroll: { flexGrow: 0 }, stack: { gap: 12 },
  reactions: { width: '100%', maxWidth: 296, borderRadius: 32, borderCurve: 'continuous', paddingHorizontal: 8, borderWidth: StyleSheet.hairlineWidth },
  preview: { maxWidth: '88%', borderRadius: 24, borderCurve: 'continuous', paddingHorizontal: 16, paddingVertical: 12 },
  previewText: { fontSize: 16, lineHeight: 23 },
  actions: { width: '100%', maxWidth: 240, borderRadius: 32, borderCurve: 'continuous', paddingVertical: 12, borderWidth: StyleSheet.hairlineWidth },
  row: { flexDirection: 'row', alignItems: 'center', gap: 14, paddingHorizontal: 18, paddingVertical: 13, minHeight: 48 },
  label: { flex: 1, minWidth: 0 }, text: { fontSize: 17, lineHeight: 24 }, note: { fontSize: 13, lineHeight: 19 }, disabled: { opacity: 0.45 },
});
